import { getMcqOtherCountryDialCode } from './mcqOtherCountryDialCodes';
import type {
  McqOtherDateFormat,
  McqOtherOptionConfig,
  McqQuestion,
  McqOption,
} from '../../fetchSurvey/types/mcq';
import type { AnswerValue, SurveyAnswers } from '../../submitSurvey/types';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Parse a formatted date string per `dateFormat` into a local Date. */
export function parseMcqOtherDateValue(text: string, format: McqOtherDateFormat): Date | null {
  const trimmed = text.trim();
  let day: number;
  let month: number;
  let year: number;

  const parts = trimmed.split(/[/.-]/).map(part => Number(part));
  if (parts.length !== 3 || parts.some(part => !Number.isFinite(part))) {
    return null;
  }

  if (format === 'DD/MM/YYYY') {
    [day, month, year] = parts;
  } else if (format === 'MM/DD/YYYY') {
    [month, day, year] = parts;
  } else {
    [year, month, day] = parts;
  }

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

/** Format a Date to the SDK contract string per `dateFormat`. */
export function formatMcqOtherDateValue(date: Date, format: McqOtherDateFormat): string {
  const day = pad2(date.getDate());
  const month = pad2(date.getMonth() + 1);
  const year = String(date.getFullYear());

  if (format === 'DD/MM/YYYY') {
    return `${day}/${month}/${year}`;
  }
  if (format === 'MM/DD/YYYY') {
    return `${month}/${day}/${year}`;
  }
  return `${year}/${month}/${day}`;
}

/** API option-level key: `{questionId}-:-{optionId}` */
export function buildMcqOptionLevelKey(questionId: string, optionId: string): string {
  return `${questionId}-:-${optionId}`;
}

export function findMcqOtherOption(question: McqQuestion): McqOption | undefined {
  return question.options.find(option => option.otherOption !== undefined);
}

export function resolveMcqOptionBySelectionEntry(
  question: McqQuestion,
  entry: string
): McqOption | undefined {
  return (
    question.options.find(option => option.id === entry) ??
    question.options.find(option => option.optionLabel === entry)
  );
}

export function resolveMcqSelectedOptionIds(
  question: McqQuestion,
  value: AnswerValue
): string[] {
  const raw: string[] = Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : typeof value === 'string'
      ? [value]
      : [];

  return raw.map(entry => resolveMcqOptionBySelectionEntry(question, entry)?.id ?? entry);
}

function mcqOtherSelectionEntries(selection: unknown): string[] {
  if (Array.isArray(selection)) {
    return selection.filter((entry): entry is string => typeof entry === 'string');
  }
  if (typeof selection === 'string') {
    return [selection];
  }
  return [];
}

export function isMcqOtherOptionSelected(
  question: McqQuestion,
  selection: unknown,
  otherOptionId: string
): boolean {
  const otherOption = question.options.find(option => option.id === otherOptionId);
  const matchValues = new Set<string>([otherOptionId]);
  if (otherOption?.optionLabel) {
    matchValues.add(otherOption.optionLabel);
  }

  return mcqOtherSelectionEntries(selection).some(entry => matchValues.has(entry));
}

/** Normalize stored Other text to the API submit contract per content type. */
export function formatMcqOtherSubmitText(
  config: McqOtherOptionConfig,
  text: string
): string {
  const trimmed = text.trim();

  switch (config.contentType) {
    case 'PHONE_NUMBER': {
      if (/^\+\d/.test(trimmed)) {
        return trimmed;
      }

      const parsed = parseMcqOtherCountryValue(trimmed);
      if (!parsed) {
        return trimmed;
      }

      const dialCode = getMcqOtherCountryDialCode(parsed.countryCode);
      const local = parsed.value.trim();
      if (!dialCode || !local) {
        return trimmed;
      }

      return `+${dialCode} ${local}`;
    }
    case 'POSTAL_CODE': {
      const postalMatch = trimmed.match(/^([A-Za-z]{2})\s+(.+)$/);
      if (postalMatch) {
        return `${postalMatch[1].toUpperCase()} ${postalMatch[2]}`;
      }

      const parsed = parseMcqOtherCountryValue(trimmed);
      if (!parsed) {
        return trimmed;
      }

      const local = parsed.value.trim();
      if (!local) {
        return trimmed;
      }

      return `${parsed.countryCode.toUpperCase()} ${local}`;
    }
    case 'DATE': {
      if (/^\d{10,13}$/.test(trimmed)) {
        return trimmed;
      }

      const date = parseMcqOtherDateValue(trimmed, config.dateFormat);
      if (!date) {
        return trimmed;
      }

      return String(date.getTime());
    }
    default:
      return trimmed;
  }
}

export function seedSelectedMcqOtherText(
  question: McqQuestion,
  answers: SurveyAnswers,
  selection: unknown
): SurveyAnswers {
  const next = { ...answers };

  for (const option of question.options) {
    if (!option.otherOption) {
      continue;
    }

    if (!isMcqOtherOptionSelected(question, selection, option.id)) {
      continue;
    }

    const optionLevelKey = buildMcqOptionLevelKey(question.id, option.id);
    const existing = next[optionLevelKey];
    if (typeof existing === 'string' && existing.trim() !== '') {
      continue;
    }

    const config = option.otherOption;
    if (config.contentType === 'PHONE_NUMBER' || config.contentType === 'POSTAL_CODE') {
      const defaultCountry = config.countryCodes[0];
      if (defaultCountry) {
        next[optionLevelKey] = formatMcqOtherCountryValue(defaultCountry.toUpperCase(), '');
      }
    }
  }

  return next;
}

export function getMcqOtherOptionLevelKeys(question: McqQuestion): string[] {
  return question.options
    .filter(option => option.otherOption !== undefined)
    .map(option => buildMcqOptionLevelKey(question.id, option.id));
}

/** Stored as `countryCode|localValue` for phone/postal types. */
export function parseMcqOtherCountryValue(text: string): { countryCode: string; value: string } | null {
  const separatorIndex = text.indexOf('|');
  if (separatorIndex <= 0) {
    return null;
  }
  return {
    countryCode: text.slice(0, separatorIndex),
    value: text.slice(separatorIndex + 1),
  };
}

export function formatMcqOtherCountryValue(countryCode: string, value: string): string {
  return `${countryCode}|${value}`;
}

export function getParentQuestionIdFromOptionLevelKey(optionLevelKey: string): string | undefined {
  const separatorIndex = optionLevelKey.indexOf('-:-');
  if (separatorIndex <= 0) {
    return undefined;
  }
  return optionLevelKey.slice(0, separatorIndex);
}

export function clearDeselectedMcqOtherText(
  question: McqQuestion,
  answers: SurveyAnswers,
  nextSelection: unknown
): SurveyAnswers {
  let next = { ...answers };

  for (const option of question.options) {
    if (!option.otherOption) {
      continue;
    }

    const optionLevelKey = buildMcqOptionLevelKey(question.id, option.id);
    const isSelected = isMcqOtherOptionSelected(question, nextSelection, option.id);

    if (!isSelected && next[optionLevelKey] !== undefined) {
      const { [optionLevelKey]: _, ...rest } = next;
      next = rest;
    }
  }

  return next;
}
